import { useContext, useEffect, useRef, useState } from "react";
import { FiltersManager } from "../../../../components/FiltersManager/FiltersManager";
import { NavBar } from "../../../../components/NavBar/NavBar";
import { SortingButton } from "../../../../components/Sorting/SortingButton";
import { SortingHeader } from "../../../../components/Sorting/SortingHeader";
import { SortingProvider } from "../../../../contexts/SortingContext";
import "./quoteEntry.scss";
import { QuoteOrderItem } from "./QuoteOrderItem/QuoteOrderItem";
import {
  emitQuoteEntry,
  getQuoteList,
  updateRFQDetails,
} from "../../Services/quoteEntryService";
import { environment } from "../../../../environments/environment";
import { HTTP_RESPONSE_CODES } from "../../../../globals/globals";
import { useDispatch, useSelector } from "react-redux";
import {
  removeQuoteDetails,
  setQuotes,
  updateQuoteDetail,
} from "../../../../redux/features/quoteEntry/quoteEntrySlice";
import { useSnackBar } from "../../../../hooks/useSnackBar";
import { DialogContext } from "../../../../contexts/DialogContext";
import { SendQuoteWarnig } from "./SendQuoteWarningModal/SendQuoteWarnig";
import { FiltersAndSorting } from "../../../../components/FiltersAndSorting/FiltersAndSorting";
import Skeleton from "react-loading-skeleton";
import { ResultsNotFound } from "../../../../components/ResultsNotFound/ResultsNotFound";

export const QuoteEntry = () => {
  const getCompanyFilter = () => {
    let companiesId = JSON.parse(localStorage.getItem("u_companies")).map(
      (e) => {
        return e.id;
      }
    );
    return {
      columnName: "company_id",
      value: companiesId,
    };
  };

  const buildFilterBody = (filters = []) => {
    const filtered = filters.filter(
      (item) => item && item.columnName !== "company_id"
    );

    return [getCompanyFilter(), ...filtered];
  };
  const [quoteList, setQuoteList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxPages, setMaxPages] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(environment.ElemensPerPage);
  const [viewFilters, setViewFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [sortingOrder, setSortingOrder] = useState("id");
  const [body, setBody] = useState(buildFilterBody());
  const { successMsg, warningMsg } = useSnackBar();
  const detailsByQuoteId = useSelector(
    (state) => state.quoteEntry.detailsByQuoteId
  );
  const dispatch = useDispatch();
  const { openDialog, closeDialog } = useContext(DialogContext);
  const debounceRef = useRef(null);
  const [selectedQuotes, setSelectedQuotes] = useState({});

  // functions
  const getQuotes = async (
    p = page,
    ps = pageSize,
    searchValue = search,
    order = sortingOrder,
    isScroll = false,
    filters = body
  ) => {
    setLoading(true);
    try {
      let request = await getQuoteList(p, ps, searchValue, order, filters);
      if (request.data.responseCode == HTTP_RESPONSE_CODES.SUCCESS) {
        setMaxPages(request.data.totalPages);
        if (!isScroll) {
          setPage(1);
          setQuoteList([...request.data.load]);
          dispatch(setQuotes([...request.data.load]));
        } else {
          setPage((prev) => prev + 1);
          setQuoteList((prev) => [...prev, ...request.data.load]);
          dispatch(setQuotes([...quoteList, ...request.data.load]));
        }
      }
    } catch (error) {
      console.log(error, "Error getting quotes");
    } finally {
      setLoading(false);
    }
  };

  const buildRequestBody = () => {
    let allDetails = [];

    Object.entries(detailsByQuoteId).forEach(([quoteId, details]) => {
      details.forEach((item) => {
        allDetails.push({
          id: item.id,
          responseDate: new Date(),
          unitCost: parseFloat(item?.unit_cost),
          taxIncluded: parseFloat(item?.tax_included),
          leadDays: item.lead_days,
          unitCostdiscountApplied: parseFloat(item?.discount_applied),
          currencyId: parseInt(item?.currency_id),
          duty: item.duty,
          freightCost: item.freight_cost,
        });
      });
    });

    return allDetails;
  };

  const sendQuote = async () => {
    const isValid = validateRFQ();
    if (!isValid) {
      warningMsg(
        "Invalid Data",
        "All RFQ details must have a valid Unit Cost (greater than 0)."
      );
      return;
    }

    const requestBody = buildRequestBody();

    if (requestBody.length === 0) {
      warningMsg("Nothing Selected", "Please select at least one RFQ to send.");
      return;
    }

    let response = await emitQuoteEntry(requestBody);
    if (response.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      successMsg("Quote Sent", "Quote has been sent successfully.");
      getQuotes(0, pageSize, search, sortingOrder, false, body);
      closeDialog();
      setSelectedQuotes({});
      const sentQuoteIds = Object.keys(selectedQuotes);
      dispatch(removeQuoteDetails(sentQuoteIds));
    }
  };

  const openWarningDialog = () => {
    openDialog({
      component: SendQuoteWarnig,
      props: {
        onClose: closeDialog,
        send: sendQuote,
      },
    });
  };

  const validateRFQ = () => {
    const entries = Object.entries(detailsByQuoteId);

    for (const [quoteId, details] of entries) {
      for (const detail of details) {
        const cost = detail.unit_cost;
        if (
          cost === null ||
          cost === undefined ||
          cost === "" ||
          Number(cost) === 0
        ) {
          console.warn(
            `RFQ ${quoteId} has a detail without a valid unit cost.`
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleSearchOfQuotes = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      //   setPage(0);
      getQuotes(0, pageSize, search, sortingOrder, false, body);
    }, environment.debounceTime);
  };

  const sectionQuotesListRef = useRef(null);

  const handleScroll = () => {
    const section = sectionQuotesListRef.current;
    if (section) {
      const { scrollTop, scrollHeight, clientHeight } = section;
      if (scrollTop + clientHeight >= scrollHeight - 2) {
        getMoreQuotes();
      }
    }
  };

  const getMoreQuotes = () => {
    if (loading || page >= maxPages) {
      return;
    }
    getQuotes(page, pageSize, search, sortingOrder, true, body);
  };

  useEffect(() => {
    const section = sectionQuotesListRef.current;
    if (section) {
      section.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (section) {
        section.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  //effects
  useEffect(() => {
    // setPage(0)
    getQuotes(0, pageSize, search, sortingOrder, false, body);
  }, [sortingOrder]);

  useEffect(() => {
    getQuotes();
  }, []);
  useEffect(() => {
    setPage(0);
    handleSearchOfQuotes();
  }, [search]);

  // useEffect(() => {
  //     validateQuoteDetails()
  // }, [detailsByQuoteId])

  useEffect(() => {
    let data = Object.entries(selectedQuotes);
  }, [selectedQuotes]);

  const handleQuoteSelect = (quoteId, details) => {
    setSelectedQuotes((prev) => {
      const copy = { ...prev };
      if (!details) {
        delete copy[quoteId];
      } else {
        copy[quoteId] = details;
      }
      return copy;
    });
  };
  const [companyDropData, setCompanyDropData] = useState(
    JSON.parse(localStorage.getItem("u_companies"))
  );

  const [filtersDefinition, setFiltersDefinition] = useState([
    {
      id: 138,
      columnName: "id",
      isDate: false,
      columnSelection: "id",
    },
    {
      id: 112,
      columnName: "order_type",
      isDate: false,
      columnSelection: "id",
    },
    {
      id: 87,
      columnName: "location_id",
      isDate: false,
      columnSelection: "id",
    },
    {
      id: 102,
      columnName: "site_id",
      isDate: false,
      columnSelection: "id",
    },
    {
      id: 113,
      columnName: "priority",
      isDate: false,
      columnSelection: "name",
    },
    {
      id: 91,
      columnName: "company_id",
      isDate: false,
      columnSelection: "id",
      isStatic: true,
      staticData: companyDropData,
    },
    {
      id: 139,
      columnName: "status",
      isDate: false,
      columnSelection: "status",
    },
  ]);

  const handleFilters = (data) => {
    let hasCompanyFilter = data.some((e) => e.columnName == "company_id");

    if (hasCompanyFilter) {
      setBody(data);
      getQuotes(0, pageSize, search, sortingOrder, false, data);
    } else {
      const updatedBody = buildFilterBody(data);
      setBody(updatedBody);
      getQuotes(0, pageSize, search, sortingOrder, false, updatedBody);
    }
  };
  return (
    <SortingProvider
      properties={{
        id: "RFQ",
        order_type: "Order Type",
        location_id: "Location",
        site_id: "Site",
        date_sent: "Date Required",
        priority: "Priority",
        company_id: "Requested By",
        requested_by: "Supply Chain",
        created_date: "Created",
        total_cost: "Total Cost",
        status: "Status",
      }}
      onOrderChange={(e) => {
        setSortingOrder(e);
      }}
      columnFilterProperties={{
        id: 138,
        order_type: 112,
        location_id: 87,
        site_id: 102,
        priority: 113,
        status: 139,
        company_id: 91,
      }}
      generalFilters={filtersDefinition}
    >
      <section className="quote-entry">
        <div className="quote-entry-nav">
          <NavBar
            dataBreadCrumbs="/quote-entry"
            moduleId={3}
            isLocalSearch
            searchActions={
              <div className="quote-entry-filters">
                <FiltersManager
                  onButtonClick={() => {
                    setViewFilters(!viewFilters);
                  }}
                ></FiltersManager>
                <SortingButton></SortingButton>
              </div>
            }
            searchInputProps={{
              type: "text",
              onTextChange: (e) => setSearch(e),
            }}
          ></NavBar>
          <FiltersAndSorting
            viewFilters={viewFilters}
            onFiltersChange={handleFilters}
          />
        </div>
        <div className="quote-entry-content">
          <div className="quote-entry-order-list" ref={sectionQuotesListRef}>
            {loading ? (
              <Skeleton count={10} height={"2.7rem"}></Skeleton>
            ) : quoteList.length > 0 ? (
              quoteList.map((e, idx) => {
                return (
                  <QuoteOrderItem
                    key={idx}
                    data={e}
                    onSelect={handleQuoteSelect}
                    isLast={idx === quoteList.length - 1}
                  ></QuoteOrderItem>
                );
              })
            ) : (
              <ResultsNotFound></ResultsNotFound>
            )}
          </div>
        </div>

        {Object.entries(selectedQuotes).length > 0 && (
          <div className="send-quote-entry-cont">
            <span>
              RFQ Selected
              <p
                style={{
                  padding: ".1rem",
                  minHeight: "1rem",
                  minWidth: "1rem",
                  background: "#00CE88",
                  color: "var(--white)",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: ".2rem",
                }}
              >
                {Object.entries(selectedQuotes).length}
              </p>
            </span>

            <button
              className="quote-entry-send-quote-button"
              onClick={openWarningDialog}
              // disabled={disableSendQuote}
              style={{
                background: "#00CE88",
                color: "var(--white)",
              }}
            >
              <p>Send Quote</p>
            </button>
          </div>
        )}
      </section>
    </SortingProvider>
  );
};
